import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';

interface SaveFilterDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (name: string) => void;
}

const SaveFilterDialog: React.FC<SaveFilterDialogProps> = ({
  open,
  onClose,
  onSave,
}) => {
  const [filterName, setFilterName] = useState('');
  const [error, setError] = useState('');

  const handleSave = () => {
    const trimmedFilterName = filterName.trim();

    // Validate the filter name
    if (trimmedFilterName === '') {
      setError('Filter name cannot be empty.');
      return;
    }
    if (trimmedFilterName.length < 3) {
      setError('Filter name must be at least 3 characters long.');
      return;
    }
    if (trimmedFilterName.length > 100) {
      setError('Filter name cannot be longer than 100 characters.');
      return;
    }

    // No validation errors; proceed to save
    setError('');
    onSave(trimmedFilterName);
    handleClose();
  };

  const handleClose = () => {
    setFilterName('');
    setError('');
    onClose();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFilterName(value);

    // Validate as the user types
    const trimmedValue = value.trim();
    if (trimmedValue === '') {
      setError('Filter name cannot be empty.');
    } else if (trimmedValue.length < 3) {
      setError('Filter name must be at least 3 characters long.');
    } else if (trimmedValue.length > 100) {
      setError('Filter name cannot be longer than 100 characters.');
    } else {
      setError('');
    }
  };

  const isSaveDisabled = !filterName.trim() || Boolean(error);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      sx={{ '& .MuiDialog-paper': { minWidth: 400 } }}
    >
      <DialogTitle>Save Filter</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Filter Name"
          type="text"
          fullWidth
          variant="outlined"
          value={filterName}
          onChange={handleChange}
          error={Boolean(error)}
          helperText={error || ' '}
          inputProps={{ maxLength: 100 }}
        />
        <Typography variant="caption" color="textSecondary" align="right">
          {filterName.length}/100 characters
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={handleSave}
          disabled={isSaveDisabled}
          variant="contained"
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SaveFilterDialog;
